import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { CartContext } from "@cart-client/contexts/CartContext";
import {
    AnalyticsContext,
    CartOverlaysContext,
    ProceedMode,
    SessionVisibility,
    useLocalStorageBasedFrequencyCapping,
} from "@swiggy-private/checkout-core";
import { GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";
import { impressionToingRtbBanner } from "@cart-client/analytics/events/cartHeaderLottie";

import { TOING_RTB_HEADER_LOTTIE_STORAGE_KEY } from "./constants";
import { getToingRtbRxAndSessionIds } from "./helpers";

export interface UseToingRtbHeaderLottieParams {
    isRtbXpEnabled: boolean;
}

export interface UseToingRtbHeaderLottieReturn {
    shouldPlayAnimation: boolean;
    shouldShowLottie: boolean;
    showRtbFinalState: boolean;
    onAnimationComplete: VoidFunction;
    isWaitingForTurn: boolean;
    isAnimationInProgress: boolean;
}

export const useToingRtbHeaderLottie = ({
    isRtbXpEnabled,
}: UseToingRtbHeaderLottieParams): UseToingRtbHeaderLottieReturn => {
    const { cartState } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);

    const { rxId, sid } = useMemo(() => getToingRtbRxAndSessionIds(cartState.reviewedCart), [cartState.reviewedCart]);
    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: TOING_RTB_HEADER_LOTTIE_STORAGE_KEY,
        id: rxId && sid ? `${rxId}-${sid}` : undefined,
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
        maxFrequency: 1,
    });

    const shouldRegister = isRtbXpEnabled && !!rxId && !!sid && shouldProceed({ mode: ProceedMode.ONLY_CHECK });

    const isRegisteredRef = useRef(false);
    const hasCompletedRef = useRef(false);
    const [isComplete, setIsComplete] = useState(false);
    const onCloseOverlayRef = useRef(onCloseOverlay);
    onCloseOverlayRef.current = onCloseOverlay;
    const enabledOverlayIdRef = useRef(enabledOverlayId);
    enabledOverlayIdRef.current = enabledOverlayId;

    useEffect(() => {
        hasCompletedRef.current = false;
        setIsComplete(false);
        isRegisteredRef.current = false;
    }, [rxId, sid]);

    useEffect(() => {
        if (!shouldRegister) {
            return;
        }

        registerOverlays({
            overlayType: OverlayType.LOTTIE,
            priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
            id: LottieIds.TOING_RTB_HEADER_LOTTIE,
        });
        isRegisteredRef.current = true;
    }, [shouldRegister, registerOverlays, rxId, sid]);

    useEffect(() => {
        return () => {
            if (isRegisteredRef.current && enabledOverlayIdRef.current === LottieIds.TOING_RTB_HEADER_LOTTIE) {
                onCloseOverlayRef.current();
            }
        };
    }, []);

    const onAnimationComplete = useCallback(() => {
        if (hasCompletedRef.current) {
            return;
        }
        hasCompletedRef.current = true;

        impressionToingRtbBanner(impressionEvent, {
            sid,
            cartId: String(cartState.reviewedCart?.data?.cartId ?? ""),
            originalCartId: String(cartState.reviewedCart?.data?.originalCartId ?? ""),
        });
        setIsComplete(true);
        shouldProceed();
        if (enabledOverlayId === LottieIds.TOING_RTB_HEADER_LOTTIE) {
            onCloseOverlay();
        }
    }, [shouldProceed, enabledOverlayId, onCloseOverlay]);

    const isOurTurn = enabledOverlayId === LottieIds.TOING_RTB_HEADER_LOTTIE;
    const isWaitingForTurn = shouldRegister && !isComplete && Boolean(enabledOverlayId) && !isOurTurn;

    const shouldPlayAnimation = shouldRegister && !isComplete && isOurTurn;
    const isAnimationInProgress = shouldRegister && !isComplete;
    const showRtbFinalState = isRtbXpEnabled && (isComplete || !shouldRegister || isWaitingForTurn);
    return {
        shouldPlayAnimation,
        shouldShowLottie: shouldPlayAnimation || showRtbFinalState,
        showRtbFinalState,
        onAnimationComplete,
        isWaitingForTurn,
        isAnimationInProgress,
    };
};
